import { useState } from 'react'
import { Input, Checkbox, Button, Typography, Spinner } from '@material-tailwind/react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionContext } from '../contexts/SessionContext'
import { updateUser, updateUserPassword, redeemUpgradeCode } from '../utils/helpers/supabase.helpers'
import { useGeneralToast } from '../hooks/toast'
import RedeemCodeMessage from './RedeemCodeMessage'
import { useDarkMode } from '../contexts/DarkModeContext'

interface FinalizeUserFormProps {
  userType?: string
}

const FinalizeUserForm: React.FC<FinalizeUserFormProps> = ({ userType }) => {
  const { t } = useTranslation()
  const { error: toastError } = useGeneralToast()
  const { darkMode } = useDarkMode()
  const [searchParams] = useSearchParams()
  const codeParam = searchParams.get('redeemCode')
  const [redeemCode, setRedeemCode] = useState<string | null>(codeParam)
  const [redeemCodeError, setRedeemCodeError] = useState<string | null>(null)

  const { refresh, user } = useSessionContext()

  const [formData, setFormData] = useState<Record<string, string | boolean>>({})
  const [password, setPassword] = useState<string>()
  const [passwordConfirm, setPasswordConfirm] = useState<string>()

  const [loading, setLoading] = useState(false)

  const handleInputChange = (key: string, value: string | boolean) => {
    setFormData((current) => ({
      ...current,
      [key]: value,
    }))
  }

  const handleRedeemCode = async (code: string | null) => {
    if (code && user?.email) {
      const { data: redeemData, error: redeemError } = await redeemUpgradeCode({
        code,
        email: user.email,
      })

      if (redeemError) {
        setRedeemCodeError(
          typeof redeemError === 'string' ? redeemError : (redeemError as Error)?.message || 'Unknown error'
        )
        return { redeemError }
      }

      const redeemedRole = (redeemData as { target_role?: number })?.target_role

      return { redeemedRole }
    }
    return {}
  }

  const updateUserData = async (e: React.FormEvent) => {
    setLoading(true)
    e.preventDefault()
    e.stopPropagation()

    const { error: updateUserPasswordError } = await updateUserPassword({
      password: password || '',
      passwordConfirm: passwordConfirm || '',
    })

    if (updateUserPasswordError) {
      toastError(
        typeof updateUserPasswordError === 'string'
          ? updateUserPasswordError
          : ((updateUserPasswordError as Error)?.message ?? JSON.stringify(updateUserPasswordError))
      )
      setLoading(false)
      return
    }

    const { redeemError, redeemedRole } = await handleRedeemCode(redeemCode)

    if (redeemError) {
      setLoading(false)
      return
    }

    const bch_model = userType === 'indiegogo' ? 'MezzoForte OpenEar' : formData.bch_model

    if (!user?.id) {
      toastError('User not found')
      setLoading(false)
      return
    }

    const { error: updateUserError } = await updateUser({
      attributes: {
        ...formData,
        bch_model,
        role_id: redeemedRole,
        finalized: true,
      },
      id: user.id,
    })

    if (updateUserError) {
      toastError(
        typeof updateUserError === 'string'
          ? updateUserError
          : ((updateUserError as Error)?.message ?? JSON.stringify(updateUserError))
      )
      setLoading(false)
      return
    }

    setLoading(false)
    refresh()
  }

  return (
    <form className="w-full md:w-1/2 flex flex-col gap-4 mb-6" onSubmit={updateUserData}>
      {codeParam && (
        <div>
          <Input
            {...(darkMode && { color: 'white' })}
            className="font-bold"
            value={redeemCode || ''}
            readOnly
            type="text"
            label={t('finalize.earlyBirdCode')}
            required
            success={!redeemCodeError}
            error={!!redeemCodeError}
          />
          <RedeemCodeMessage
            redeemCodeError={redeemCodeError}
            setRedeemCode={setRedeemCode}
            setRedeemCodeError={setRedeemCodeError}
            urlParamCode={codeParam || ''}
            textColor="white"
          />
        </div>
      )}

      {/* redeemCode is NOT in the URL but user wants to insert it */}
      {!codeParam && userType === 'indiegogo' && (
        <div>
          <Input
            {...(darkMode && { color: 'white' })}
            className="font-bold"
            value={redeemCode || ''}
            onChange={(e) => setRedeemCode(e.currentTarget.value)}
            type="text"
            label={t('finalize.earlyBirdCode')}
            required
            success={!redeemCodeError}
            error={!!redeemCodeError}
          />
          <RedeemCodeMessage
            redeemCodeError={redeemCodeError}
            setRedeemCode={setRedeemCode}
            setRedeemCodeError={setRedeemCodeError}
            urlParamCode={codeParam}
            textColor="white"
          />
        </div>
      )}

      {userType === 'bch-owner' && (
        <>
          <Input
            {...(darkMode && { color: 'white' })}
            value={(formData.bch_model as string) || ''}
            onChange={(e) => handleInputChange('bch_model', e.currentTarget.value)}
            type="text"
            label={t('finalize.bchModel')}
            required
          />
          <Typography className="font-italic mt-[-0.5rem]" variant="small" color="white">
            {t('finalize.bchModelHelp')}
          </Typography>
        </>
      )}

      <Input
        {...(darkMode && { color: 'white' })}
        value={user?.email || ''}
        readOnly
        type="email"
        label={t('profile.email')}
      />
      <Input
        {...(darkMode && { color: 'white' })}
        value={(formData.name as string) || ''}
        onChange={(e) => handleInputChange('name', e.currentTarget.value)}
        type="text"
        label={t('profile.name')}
        required
      />
      <Input
        {...(darkMode && { color: 'white' })}
        value={(formData.surname as string) || ''}
        onChange={(e) => handleInputChange('surname', e.currentTarget.value)}
        type="text"
        label={t('profile.surname')}
        required
      />
      <Input
        {...(darkMode && { color: 'white' })}
        value={(formData.company as string) || ''}
        onChange={(e) => handleInputChange('company', e.currentTarget.value)}
        type="text"
        label={t('profile.company')}
      />
      <div>
        <Input
          {...(darkMode && { color: 'white' })}
          value={password || ''}
          onChange={(e) => setPassword(e.currentTarget.value)}
          type="password"
          label={t('password.newPassword')}
          required
        />
        <Typography variant="small" className="mt-1">
          {t('password.passwordRequirements')}
        </Typography>
      </div>
      <Input
        {...(darkMode && { color: 'white' })}
        value={passwordConfirm || ''}
        onChange={(e) => setPasswordConfirm(e.currentTarget.value)}
        type="password"
        label={t('password.confirmPassword')}
        required
      />
      <div className="flex justify-start items-center w-full ml-[-0.75rem]">
        <Checkbox
          id="terms_conditions"
          checked={Boolean(formData.terms_accepted)}
          onChange={(e) => handleInputChange('terms_accepted', e.currentTarget.checked)}
          type="checkbox"
          required
        />
        <label htmlFor="terms_conditions">
          <Typography variant="small">{t('finalize.acceptTerms')}</Typography>
        </label>
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? <Spinner className="h-4 w-4 mx-auto" /> : t('finalize.createAccount')}
      </Button>
    </form>
  )
}

export default FinalizeUserForm
